import { useState, useMemo, useCallback } from "react"
import { columns } from "../crewMembersTable"

/**
 * Custom hook for managing crew member filtering, sorting, and pagination
 * @param {Array} crewData - Raw crew member data
 * @param {Function} t - Translation function
 * @returns {Object} Filter states, filtered data, and handler functions
 */
export const useCrewFilters = (crewData, t) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [search, setSearch] = useState("")
  const [selectedRoles, setSelectedRoles] = useState([])
  const [selectedStatuses, setSelectedStatuses] = useState([])
  const [sorter, setSorter] = useState({ columnKey: null, order: null })

  // Role filter options
  const roleOptions = useMemo(
    () => [
      { label: t("All") || "All", value: "all" },
      { label: t("Admin") || "Admin", value: "admin" },
      { label: t("Captain") || "Captain", value: "captain" },
      { label: t("Crew Member") || "Crew Member", value: "crew_member" },
      { label: t("Guide") || "Guide", value: "guide" },
      { label: t("Office Manager") || "Office Manager", value: "office_manager" }
    ],
    [t]
  )

  // Status filter options
  const statusOptions = useMemo(
    () => [
      { label: t("All") || "All", value: "all" },
      { label: t("Available") || "Available", value: "available" },
      { label: t("Disabled") || "Disabled", value: "disabled" }
    ],
    [t]
  )

  // Expand selected items when "all" is selected
  const expandedSelectedRoles = useMemo(() => {
    if (selectedRoles.includes("all")) {
      return roleOptions.filter((r) => r.value !== "all").map((r) => r.value)
    }
    return selectedRoles
  }, [selectedRoles, roleOptions])

  const expandedSelectedStatuses = useMemo(() => {
    if (selectedStatuses.includes("all")) {
      return statusOptions.filter((s) => s.value !== "all").map((s) => s.value)
    }
    return selectedStatuses
  }, [selectedStatuses, statusOptions])

  // Helper function to derive status from row data
  const deriveStatus = useCallback((row) => {
    if (row.status) return row.status.toLowerCase()
    const avail = row.availability?.toLowerCase() || ""
    if (/day off|time off|off till|off until|dayoff/.test(avail)) return "disabled"
    return "available"
  }, [])

  // Filtered data
  const filteredData = useMemo(() => {
    let data = crewData
    const searchLower = search.toLowerCase()

    if (searchLower) {
      data = data.filter(
        (row) =>
          row.title?.toLowerCase().includes(searchLower) ||
          row.role?.toLowerCase().includes(searchLower) ||
          row.name?.toLowerCase().includes(searchLower) ||
          deriveStatus(row).includes(searchLower)
      )
    }

    if (selectedRoles.length && !selectedRoles.includes("all")) {
      const normalize = (str) => str?.toLowerCase().replaceAll(/\s+/g, "_")
      data = data.filter((row) => selectedRoles.includes(normalize(row.role)))
    }

    if (selectedStatuses.length && !selectedStatuses.includes("all")) {
      data = data.filter((row) => selectedStatuses.includes(deriveStatus(row)))
    }

    return data
  }, [crewData, search, selectedRoles, selectedStatuses, deriveStatus])

  // Sorted data
  const sortedData = useMemo(() => {
    if (!sorter.columnKey || !sorter.order) return filteredData
    const col = columns.find((c) => c.key === sorter.columnKey)
    if (!col?.sorter) return filteredData
    const sorted = [...filteredData].sort(col.sorter)
    if (sorter.order === "descend") sorted.reverse()
    return sorted
  }, [filteredData, sorter])

  // Paginated data
  const paginatedData = useMemo(() => {
    return sortedData.slice((currentPage - 1) * pageSize, currentPage * pageSize)
  }, [sortedData, currentPage, pageSize])

  const handleSearchChange = useCallback((value) => {
    setSearch(value)
    setCurrentPage(1)
  }, [])

  const handleRemoveRole = useCallback(
    (roleValue) => {
      setSelectedRoles((prev) => {
        if (prev.includes("all")) {
          return roleOptions.filter((r) => r.value !== "all" && r.value !== roleValue).map((r) => r.value)
        }
        return prev.filter((v) => v !== roleValue)
      })
    },
    [roleOptions]
  )

  const handleRemoveStatus = useCallback(
    (statusValue) => {
      setSelectedStatuses((prev) => {
        if (prev.includes("all")) {
          return statusOptions.filter((s) => s.value !== "all" && s.value !== statusValue).map((s) => s.value)
        }
        return prev.filter((v) => v !== statusValue)
      })
    },
    [statusOptions]
  )

  const handleSortChange = useCallback((_, __, sorterObj) => {
    setSorter({ columnKey: sorterObj.columnKey, order: sorterObj.order })
    setCurrentPage(1)
  }, [])

  return {
    // State
    search,
    selectedRoles,
    selectedStatuses,
    currentPage,
    pageSize,
    // Derived data
    roleOptions,
    statusOptions,
    expandedSelectedRoles,
    expandedSelectedStatuses,
    filteredData,
    paginatedData,
    // Handlers
    setSearch: handleSearchChange,
    setSelectedRoles,
    setSelectedStatuses,
    handleRemoveRole,
    handleRemoveStatus,
    handleSortChange,
    setCurrentPage,
    setPageSize
  }
}
